import React from 'react';

import {ProcedureTaskCore} from 'sweet-taskinfo';

import {DocHeaderStructure} from 'app/general/structures/DocHeader';
import {DocumentMetadataStatusButtons} from 'app/general/structures/DocumentMetadataStatusButtons';
import MetaContentTypeName from 'app/general/structures/MetaContentTypeName';
import {UserContentContainer} from 'app/general/structures/UserContentContainer';
import {ImagesCarousel} from 'app/general/widgets/ImagesCarousel';
import {DocumentPolicyList} from 'app/policies/components/DocumentPolicyList';
import {validateDescription} from 'app/procedures/utils';
import {useAccountSettingsState} from 'app/settings/api';
import {DocumentTagsList} from 'app/tags/components/DocumentTagsList';
import {getProcedureName} from 'app/utils/fallbacks';
import {isRightToLeftWrapper} from 'app/utils/rtl';

import {TaskSubscribedStatusButton} from './TaskSubscribedStatusButton';
import {ViewTaskMetaDataExtra} from './ViewTaskMetaDataExtra';

import {
  ProcedureParsedContent,
  ProcedureSerializedType,
  TaskInstanceSerializedType,
  UpdateTaskPayloadType,
} from 'sweet-types';

interface BaseProps {
  taskCore: ProcedureTaskCore;
  isTaskBlocked?: boolean;
  updateTaskInstance: (
    task: TaskInstanceSerializedType,
    changes: UpdateTaskPayloadType
  ) => Promise<unknown>;
  setWarnIncomplete: (status: boolean) => any;
  warningComponent?: JSX.Element | null;
}

interface PropsWithProcedure extends BaseProps {
  procedure: ProcedureSerializedType;
}

interface PropsWithCustomStatusContent extends BaseProps {
  customStatusContent: React.ReactElement;
}
type Props =
  | PropsWithProcedure
  | PropsWithCustomStatusContent
  | (PropsWithProcedure & PropsWithCustomStatusContent);

export const ViewTaskMetaData = ({
  taskCore,
  isTaskBlocked,
  updateTaskInstance,
  setWarnIncomplete,
  warningComponent,
  ...props
}: Props): JSX.Element => {
  const {MEDIA_URL, MEDIA_URL_READ} = useAccountSettingsState().data!;

  const {
    task,
    titles: [title, subTitle],
  } = taskCore;

  function stopImagesClickPropagation(e: React.MouseEvent<HTMLElement>): void {
    // allow users
    e.stopPropagation();
  }

  const description = normalizeDescription(
    taskCore.hasVersion === true ? taskCore.version.description : null
  );

  const carousel = [...description.images, ...description.embeds];

  return (
    <DocHeaderStructure
      warningContent={warningComponent}
      advancedContent={
        <MetaContentTypeName
          contentType="taskinstance"
          statusContent={
            'customStatusContent' in props ? (
              props.customStatusContent
            ) : (
              <>
                <TaskSubscribedStatusButton taskTemplate={task.template} />
                <DocumentMetadataStatusButtons
                  canEdit={false}
                  contentType="procedure"
                  context="task"
                  document={props.procedure}
                />
              </>
            )
          }
        />
      }
      titleContent={
        <div
          className={isRightToLeftWrapper(title) ? 'right-to-left-text' : ''}
        >
          <h1>{getProcedureName(title, true)}</h1>
          {subTitle != null ? (
            <h4>{getProcedureName(subTitle, true)}</h4>
          ) : null}
        </div>
      }
      tagsContent={
        'procedure' in props ? (
          <DocumentTagsList tags={props.procedure.tags} />
        ) : undefined
      }
      policyContent={
        'procedure' in props ? (
          <DocumentPolicyList policies={props.procedure.policies} />
        ) : undefined
      }
      descriptionContent={
        <div>
          <UserContentContainer
            mediaURL={MEDIA_URL}
            mediaURLRead={MEDIA_URL_READ}
            content={description.content}
          />
          {carousel.length > 0 ? (
            <div onClick={stopImagesClickPropagation}>
              <ImagesCarousel images={carousel} className="step-images" />
            </div>
          ) : null}
        </div>
      }
      extraContent={
        <ViewTaskMetaDataExtra
          taskCore={taskCore}
          isTaskBlocked={isTaskBlocked}
          hasDescription={description.content !== ''}
          setWarnIncomplete={setWarnIncomplete}
          updateTaskInstance={updateTaskInstance}
        />
      }
    />
  );
};

/* description can be null, string or an object, so lets normalize it before use */

function normalizeDescription(
  description: null | string | any
): ProcedureParsedContent['description'] {
  if (description == null || typeof description === 'string') {
    return validateDescription({
      content: description || '',
    });
  }

  return validateDescription(description);
}
